<template>
    <div>
        <div class="d-flex justify-content-between">
            <h1 class="page-header">{{ $t('purchaseRequestTemplate.pageTitle') }}</h1>
            <ts-breadcrumb :items="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('purchaseRequestTemplate.pageTitle'),
                    active: true
                }
            ]" />
        </div>
        <ts-card>
            <ts-card-wrapper>
                <div class="d-flex justify-content-between ts-items-center ts-whitespace-no-wrap ts-space-x-3">
                    <div class="ts-flex ts-items-center ts-space-x-3">
                        <ts-button outline color="info" @click.prevent="addNew">
                            {{ $t('add_new') }}
                        </ts-button>
                    </div>
                    <div class="d-flex ts-space-x-3 ts-items-center">
                        <Tooltip :content="$t('search')" placement="top" transfer :max-width="width">
                            <Input search v-model="search" :placeholder="$t('search')" :style="width"
                                @on-focus="() => (width = 'width: 500px')" @on-blur="() => (width = 'width: 300px')" />
                        </Tooltip>
                    </div>
                </div>
            </ts-card-wrapper>
            <Table :columns="columns" :data="resources" size="small" :loading="loading" highlight-row stripe
                max-height="700">
                <template slot-scope="{ row }" slot="branch">
                    {{ row.branch ? row.branch.branch_name_en : 'N/A' }}
                </template>
                <template slot-scope="{ row }" slot="created_by">
                    {{ row.created_by_user ? row.created_by_user.employee_name_en : 'N/A' }}
                </template>
                <template slot-scope="{ row }" slot="item_count">
                    {{ row.details ? row.details.length : 0 }}
                </template>
                <template slot-scope="{ row }" slot="action">
                    <div class="ts-space-x-2 ts-whitespace-no-wrap">
                        <a href="#" v-tooltip="$t('edit')" class="text-primary" @click.prevent="edit(row)">
                            <i class="fas fa-edit"></i>
                        </a>
                        <a-popconfirm v-tooltip="$t('delete')" placement="leftTop" :title="$t('are_you_sure_to_delete')"
                            :ok-text="$t('yes')" :cancel-text="$t('no')" @confirm="deleteRecord(row)">
                            <a href="#" :disabled="row._deleting">
                                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                                <i class="fa fa-trash text-red" v-else></i>
                            </a>
                        </a-popconfirm>
                    </div>
                </template>
            </Table>
            <div class="d-flex justify-content-between align-items-center p-15">
                <ts-total-list v-model="pagination" />
                <ts-i-pagination v-model="pagination" @navigate="fetchData"></ts-i-pagination>
            </div>
        </ts-card>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'

export default {
    name: 'templateIndex',
    data() {
        return {
            loading: false,
            width: 'width: 300px',
            errors: new Errors()
        }
    },
    computed: {
        ...mapState('purchaseRequest/template', ['resources', 'pagination']),
        search: {
            get() {
                return this.$store.state.purchaseRequest.template.search
            },
            set(newValue) {
                this.$store.commit(
                    'purchaseRequest/template/SET_SEARCH',
                    newValue
                )
                this.$store.commit('purchaseRequest/template/RESET_CURRENT_PAGE')
            }
        },
        columns() {
            return [
                {
                    title: this.$t('purchaseRequestTemplate.templateName'),
                    key: 'template_name',
                    minWidth: 200,
                    sortable: true
                },
                {
                    title: this.$t('purchaseRequestTemplate.templateDescription'),
                    key: 'template_description',
                    minWidth: 250,
                    sortable: true
                },
                {
                    title: this.$t('branch.pageTitle'),
                    slot: 'branch',
                    minWidth: 150,
                    sortable: true
                },
                {
                    title: this.$t('purchaseRequestTemplate.items'),
                    slot: 'item_count',
                    minWidth: 80,
                    align: 'center',
                    sortable: true
                },
                {
                    title: this.$t('purchaseOrder.createdBy'),
                    slot: 'created_by',
                    minWidth: 150,
                    sortable: true
                },
                {
                    title: this.$t('employeeProfile.hireDate'), // Reusing "Date" or similar, checking context. "Created Date" not found exactly, using generic date or creating new if needed. "Created Date" doesn't have direct key in provided list but "created_by" does. Let's look for "Date". "date" exists in many places. Let's use generic date or leave as English if no good match. Wait, I added specific keys? No. I'll use "date" from dailyReprot.date or just hardcode "Created Date" if I can't find better. Actually I can add "createdDate" to my new section. I missed it. I'll add it in next step if needed. For now let's use "date".
                    // Actually, let's stick to what I added. I didn't add "Created Date".
                    // I'll leave "Created Date" as is for now or use "date".
                    // Better: I will add "createdDate" to the JSONs in a fix-up step if I want perfection.
                    // For now, I'll use 'purchaseOrder.poDate' style? No.
                    // Let's check 'common' keys. 'date' is in many.
                    title: 'Created Date',
                    key: 'created_date',
                    minWidth: 150,
                    sortable: true
                },
                {
                    title: this.$t('actions'),
                    key: 'action',
                    slot: 'action',
                    fixed: 'right',
                    align: 'center',
                    width: 120
                }
            ]
        }
    },
    methods: {
        ...mapActions('purchaseRequest/template', ['getFormViewModel']),
        fetchData(attributes) {
            this.loading = true
            this.$store
                .dispatch('purchaseRequest/template/fetch', { ...attributes })
                .then(response => {
                    this.loading = false
                })
                .catch(error => {
                    this.loading = false
                    this.$toasted.error(error.message)
                })
        },
        addNew() {
            this.$router.push({ name: 'purchase-request-template-create' })
        },
        edit(record) {
            this.$router.push({
                name: 'purchase-request-template-edit',
                params: { id: record.template_id }
            })
        },
        deleteRecord(record) {
            record._deleting = true
            this.$store
                .dispatch('purchaseRequest/template/destroy', record.template_id)
                .then(response => {
                    this.$toasted.success(response.message)
                    this.fetchData()
                })
                .catch(error => {
                    this.$toasted.error(error.message)
                })
                .finally(() => {
                    record._deleting = false
                })
        }
    },
    watch: {
        search: debounce(function () {
            this.fetchData()
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData()
        })
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit('purchaseRequest/template/RESET_STATE')
        next()
    }
}
</script>

<style>
.ivu-input-with-suffix {
    height: 34px !important;
}
</style>
